'use client';

import { useReportWebVitals } from 'next/web-vitals';

import { analytics } from '@kit/analytics';

/**
 * Forwards the browser's TTFB (Time To First Byte) to PostHog as a
 * `web_vitals.ttfb` event.
 *
 * Why this exists: posthog-js web-vitals autocapture cannot report TTFB — its
 * `SupportedWebVitalsMetrics` union is `LCP | CLS | FCP | INP` only — so the
 * autocaptured `$web_vitals` events carry paint/interaction metrics but no
 * server-time signal. TTFB is what separates server time (including cold start)
 * from client time (bundle download, parse, hydrate) when diagnosing slow first
 * loads.
 *
 * Only TTFB is forwarded here: LCP/CLS/FCP/INP are already captured by
 * posthog-js, and Next's custom `Next.js-hydration` / `Next.js-render` metrics
 * are intentionally out of scope. Route breakdown comes from PostHog's automatic
 * `$current_url`, the same property the FCP/LCP `$web_vitals` events group by.
 */
export function WebVitalsReporter() {
  useReportWebVitals((metric) => {
    if (metric.name !== 'TTFB') return;

    // `trackEvent` properties are `Record<string, string | string[]>`, so the
    // numeric TTFB is stringified (matching the planning/STM analytics events).
    const properties: Record<string, string> = {
      value: String(Math.round(metric.value)),
      rating: metric.rating,
    };

    if (metric.navigationType) {
      properties.navigation_type = metric.navigationType;
    }

    void analytics.trackEvent('web_vitals.ttfb', properties);
  });

  return null;
}
